import React, { useEffect, useMemo, useState } from 'react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import Popover from '@material-ui/core/Popover'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import pluralize from 'lib/pluralize'
import TablePagination from 'components/pagination/table-pagination'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import LessonPackCard from './lesson-pack-card'
import NoLessonPacksCard from './no-lesson-packs-card'
import { useStyles } from './styles'

const YEAR_FILTER_ALL = 'all'

const ROWS_PER_PAGE = 12

const LessonPackGrid = ({ children }) => (
  <Grid container spacing={2}>
    {children}
  </Grid>
)

const LessonPackGridItem = ({ children }) => (
  <Grid item xs={12} md={6} lg={4} style={{ display: 'flex', width: '100%' }}>
    {children}
  </Grid>
)

const LessonPacksToolbar = ({
  title,
  totalCount,
  page,
  pageCount: pageCountOverride,
  onPageChange,
  actions,
}) => {
  const classes = useStyles()
  const pageCount =
    pageCountOverride ?? (Math.ceil(totalCount / ROWS_PER_PAGE) || 1)

  return (
    <Box className={classes.listToolbar}>
      <Box className={classes.listToolbarLeft}>
        <Typography
          component="h2"
          variant="subtitle1"
          className={classes.categoryLabel}
        >
          {title}
        </Typography>
        {actions}
      </Box>
      {totalCount > 0 && (
        <Box className={classes.toolbarActions}>
          <TablePagination
            page={page}
            pageCount={pageCount}
            rowsPerPage={ROWS_PER_PAGE}
            totalCount={totalCount}
            onPageChange={onPageChange}
            alwaysShowCount
          />
        </Box>
      )}
    </Box>
  )
}

const paginate = (items, page) => {
  const start = page * ROWS_PER_PAGE
  return items.slice(start, start + ROWS_PER_PAGE)
}

const getFirstPageLayout = (purchasedCount, giftedCount) => {
  const giftedOnFirstPage = Math.min(giftedCount, ROWS_PER_PAGE)
  const purchasedOnFirstPage = Math.min(
    purchasedCount,
    Math.max(0, ROWS_PER_PAGE - giftedOnFirstPage),
  )

  return { giftedOnFirstPage, purchasedOnFirstPage }
}

const getActivePagePacks = (purchasedPacks, giftedPacks, page) => {
  const { giftedOnFirstPage, purchasedOnFirstPage } = getFirstPageLayout(
    purchasedPacks.length,
    giftedPacks.length,
  )

  if (page === 0) {
    return [
      ...purchasedPacks.slice(0, purchasedOnFirstPage),
      ...giftedPacks.slice(0, giftedOnFirstPage),
    ]
  }

  const remainder = [
    ...giftedPacks.slice(giftedOnFirstPage),
    ...purchasedPacks.slice(purchasedOnFirstPage),
  ]

  return paginate(remainder, page - 1)
}

const getActivePageCount = (purchasedCount, giftedCount) => {
  const { giftedOnFirstPage, purchasedOnFirstPage } = getFirstPageLayout(
    purchasedCount,
    giftedCount,
  )
  const remainderCount =
    giftedCount - giftedOnFirstPage + purchasedCount - purchasedOnFirstPage

  if (remainderCount <= 0) {
    return 1
  }

  return 1 + Math.ceil(remainderCount / ROWS_PER_PAGE)
}

const getYearFilterOptions = years => [
  { value: YEAR_FILTER_ALL, label: 'All Years' },
  ...years.map(year => ({
    value: String(year),
    label: String(year),
  })),
]

const YearFilter = ({ years, selectedYear, onYearChange }) => {
  const classes = useStyles()
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const filterMenuOpen = Boolean(filterAnchorEl)
  const yearOptions = useMemo(() => getYearFilterOptions(years), [years])
  const hasActiveYearFilter = selectedYear !== YEAR_FILTER_ALL
  const activeYearLabel =
    yearOptions.find(option => option.value === selectedYear)?.label ||
    'Filters'

  if (years.length === 0) {
    return null
  }

  return (
    <>
      <Button
        variant="outlined"
        color="default"
        className={`${classes.filterButton} ${
          hasActiveYearFilter ? classes.filterButtonActive : ''
        }`}
        aria-controls="lesson-pack-year-filters-menu"
        aria-haspopup="true"
        aria-expanded={filterMenuOpen}
        aria-label={
          hasActiveYearFilter
            ? `Lesson pack year filter: ${activeYearLabel} selected`
            : 'Lesson pack year filter'
        }
        onClick={event => setFilterAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
      >
        {hasActiveYearFilter ? activeYearLabel : 'Filters'}
      </Button>
      <Popover
        id="lesson-pack-year-filters-menu"
        open={filterMenuOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          className: classes.filterMenuPaper,
          style: filterAnchorEl
            ? { minWidth: filterAnchorEl.clientWidth }
            : undefined,
        }}
      >
        <Box className={classes.filterMenuContent}>
          <FormControl
            component="fieldset"
            fullWidth
            className={classes.filterForm}
          >
            <FormLabel component="legend" className={classes.filterLegend}>
              Year
            </FormLabel>
            <RadioGroup
              aria-label="Year filter"
              name="year-filter"
              className={classes.filterRadioGroup}
              value={selectedYear}
              onChange={event => {
                onYearChange(event.target.value)
                setFilterAnchorEl(null)
              }}
            >
              {yearOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={classes.filterRadioLabel}
                  control={<Radio color="primary" size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      </Popover>
    </>
  )
}

const LessonPacksDisplay = ({
  selectedTab,
  activeLessonPacks,
  activeGiftedLessonPacks = [],
  usedOrExpiredLessonPacks,
  onGiftClick,
}) => {
  const classes = useStyles()
  const [page, setPage] = useState(0)
  const years = [
    ...new Set(
      usedOrExpiredLessonPacks.map(lp =>
        lp.expiresAt ? new Date(lp.expiresAt).getFullYear() : 'No expiration',
      ),
    ),
  ]
  const [selectedYear, setSelectedYear] = useState(YEAR_FILTER_ALL)
  const filteredUsedOrExpiredLessonPacks = usedOrExpiredLessonPacks.filter(
    lp => {
      if (selectedYear === YEAR_FILTER_ALL) {
        return true
      }
      if (selectedYear === 'No expiration') {
        return !lp.expiresAt
      }
      return new Date(lp.expiresAt).getFullYear() === Number(selectedYear)
    },
  )

  useEffect(() => {
    setPage(0)
  }, [selectedTab, selectedYear])

  if (selectedTab === 'past') {
    const pageCount =
      Math.ceil(filteredUsedOrExpiredLessonPacks.length / ROWS_PER_PAGE) || 1
    const safePage = Math.min(page, pageCount - 1)
    const visiblePacks = paginate(filteredUsedOrExpiredLessonPacks, safePage)

    return (
      <Box className={classes.root}>
        <LessonPacksToolbar
          title={`${filteredUsedOrExpiredLessonPacks.length} Lesson ${pluralize(
            filteredUsedOrExpiredLessonPacks.length,
            'Pack',
          )}`}
          totalCount={filteredUsedOrExpiredLessonPacks.length}
          page={safePage}
          onPageChange={setPage}
          actions={
            <YearFilter
              years={years}
              selectedYear={selectedYear}
              onYearChange={setSelectedYear}
            />
          }
        />
        {filteredUsedOrExpiredLessonPacks.length > 0 ? (
          <LessonPackGrid>
            {visiblePacks.map(lp => (
              <LessonPackGridItem key={lp.id}>
                <LessonPackCard {...lp} isUsedOrExpired />
              </LessonPackGridItem>
            ))}
          </LessonPackGrid>
        ) : (
          <NoLessonPacksCard text="Lesson packs that have been used or expired will display here." />
        )}
      </Box>
    )
  }

  const activePackCount =
    activeLessonPacks.length + activeGiftedLessonPacks.length
  const pageCount =
    getActivePageCount(
      activeLessonPacks.length,
      activeGiftedLessonPacks.length,
    ) || 1
  const safePage = Math.min(page, pageCount - 1)
  const visiblePacks = getActivePagePacks(
    activeLessonPacks,
    activeGiftedLessonPacks,
    safePage,
  )

  return (
    <Box className={classes.root}>
      <LessonPacksToolbar
        title={`${activePackCount} Lesson ${pluralize(
          activePackCount,
          'Pack',
        )}`}
        totalCount={activePackCount}
        page={safePage}
        pageCount={pageCount}
        onPageChange={setPage}
      />
      {activePackCount > 0 ? (
        <LessonPackGrid>
          {visiblePacks.map(lp => (
            <LessonPackGridItem key={lp.id}>
              <LessonPackCard
                {...lp}
                onGiftClick={lp.isGifted ? () => {} : onGiftClick}
              />
            </LessonPackGridItem>
          ))}
        </LessonPackGrid>
      ) : (
        <NoLessonPacksCard text="Lesson packs that have been purchased will display here." />
      )}
    </Box>
  )
}

export default LessonPacksDisplay
